import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type { Paged } from '@core/api/masters';
import type {
  PurchaseOrder,
  PurchaseOrderFilters,
  PurchaseOrderGlance,
  PurchaseOrderSummary,
} from '@core/api/purchaseOrders';
import {
  purchaseOrderGlancePath,
  purchaseOrderPath,
  purchaseOrdersPath,
} from '@core/api/queryPaths';

export type {
  PurchaseOrder,
  PurchaseOrderBill,
  PurchaseOrderGlance,
  PurchaseOrderLine,
  PurchaseOrderSummary,
} from '@core/api/purchaseOrders';

export const purchaseOrdersKey = ['purchase-order'] as const;

/** The acting branch's orders — the branch is in the key, so a switch never shows the last shop's. */
export function usePurchaseOrders(
  branchId: number | undefined,
  filters: PurchaseOrderFilters = {},
) {
  return useQuery({
    queryKey: [...purchaseOrdersKey, { branchId, ...filters }],
    queryFn: () => apiFetch<Paged<PurchaseOrderSummary>>(purchaseOrdersPath(filters)),
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function usePurchaseOrder(id: number | null) {
  return useQuery({
    queryKey: [...purchaseOrdersKey, 'one', id],
    queryFn: () => apiFetch<PurchaseOrder>(purchaseOrderPath(id ?? 0)),
    enabled: id !== null,
  });
}

export {
  cancelPurchaseOrder,
  closePurchaseOrder,
  createPurchaseOrder,
  updatePurchaseOrder,
} from '@core/api/purchaseCommands';

/** The open orders in four figures — the register's heading. Under the same key, so any write refreshes it. */
export function usePurchaseOrderGlance(branchId: number | undefined) {
  return useQuery({
    queryKey: [...purchaseOrdersKey, 'glance', branchId],
    queryFn: () => apiFetch<PurchaseOrderGlance>(purchaseOrderGlancePath),
    enabled: branchId !== undefined,
  });
}
